import React from 'react';
import {
  IconButton,
  Tooltip,
  Box,
  useTheme as useMuiTheme,
} from '@mui/material';
import {
  LightMode as LightModeIcon,
  DarkMode as DarkModeIcon,
  Brightness6 as Brightness6Icon,
} from '@mui/icons-material';
import { useTheme } from '../../contexts/ThemeContext';

interface ThemeToggleProps {
  size?: 'small' | 'medium' | 'large';
  variant?: 'default' | 'floating' | 'minimal';
}

export const ThemeToggle: React.FC<ThemeToggleProps> = ({ 
  size = 'medium', 
  variant = 'default' 
}) => {
  const { mode, toggleTheme } = useTheme();
  const muiTheme = useMuiTheme();

  const handleToggle = () => {
    toggleTheme(); // Simple toggle, no database save
  };

  const getIcon = () => {
    if (variant === 'minimal') {
      return <Brightness6Icon fontSize={size} />;
    }
    return mode === 'light' ? (
      <DarkModeIcon fontSize={size} />
    ) : (
      <LightModeIcon fontSize={size} />
    );
  };

  const getTooltipText = () => {
    if (variant === 'minimal') {
      return 'Toggle theme';
    }
    return mode === 'light' ? 'Switch to dark mode' : 'Switch to light mode';
  };

  const getButtonStyles = () => {
    const baseStyles = {
      transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
      borderRadius: variant === 'floating' ? '50%' : '12px',
    };

    switch (variant) {
      case 'floating':
        return {
          ...baseStyles,
          backgroundColor: muiTheme.palette.primary.main,
          color: muiTheme.palette.primary.contrastText,
          boxShadow: muiTheme.shadows[4],
          '&:hover': {
            backgroundColor: muiTheme.palette.primary.dark,
            boxShadow: muiTheme.shadows[8],
            transform: 'translateY(-2px)',
          },
        };
      
      case 'minimal':
        return {
          ...baseStyles,
          color: muiTheme.palette.text.secondary,
          '&:hover': {
            backgroundColor: muiTheme.palette.action.hover,
            color: muiTheme.palette.text.primary,
          },
        };
      
      default:
        return {
          ...baseStyles,
          backgroundColor: muiTheme.palette.background.paper,
          color: muiTheme.palette.text.primary,
          border: `1px solid ${muiTheme.palette.divider}`,
          '&:hover': {
            backgroundColor: muiTheme.palette.action.hover,
            borderColor: muiTheme.palette.primary.main,
          },
        };
    }
  };

  return (
    <Tooltip title={getTooltipText()} arrow placement="bottom">
      <IconButton
        onClick={handleToggle}
        size={size}
        sx={getButtonStyles()}
        aria-label={`Switch to ${mode === 'light' ? 'dark' : 'light'} mode`}
      >
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            transform: mode === 'dark' ? 'rotate(180deg)' : 'rotate(0deg)',
            transition: 'transform 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
          }}
        >
          {getIcon()}
        </Box>
      </IconButton>
    </Tooltip>
  );
};

export default ThemeToggle;